import React, { useEffect } from 'react';
import { useTranslationCashHack } from 'ggApp/utils/referralTranslator';
import { connect } from 'react-redux';
import { userId as userIdSelector } from 'ggApp/modules/auth/selectors';
import {
    ActiveStore,
    mainRouteWithStoreCode as mainAppRouteSelector,
    storeActive as activeStoreSelector,
} from 'ggApp/modules/store/selectors';
import { companyDashboardEnabled as companyDashboardEnabledSelector } from 'ggApp/modules/featureFlag/selectors';
import { useQuery } from '@apollo/client';
import { User, CompanySize as CompanySizeEnum } from 'ggApp/types/graphql';
import CircleLoader from 'ggApp/shared/components/CircleLoader';
import { arrowBackIconPath } from 'ggApp/shared/icons/IconPaths';
import { Link } from 'react-router';
import { sendAnalyticsEvent } from '@devsbb/analytics-client';
import { push } from 'redux-router';
import { errorMessage as errorMessageSelector } from 'ggApp/modules/company-dashboard/selectors';
import { openHelpCenter } from '@devsbb/cs-sdk';

import CompanyInfoBox from './CompanyInfoBox';
import {
    CompanyIcon,
    OptionsIcon,
    TaxIDIcon,
    UserIcon,
    CompanyBuildingIcon,
    EnvelopeIcon,
    Globe,
} from './icons/index';
import CompanyDashboardQuery from './queries/CompanyDashboard.graphql';
import {
    Container,
    Content,
    AccountButton,
    CompanyInfo,
    Name,
    RequestChangeButton,
    CompanyInfoSection,
    CapitalLettersTitle,
    Divider,
    Text,
} from './styled';

type EnhancedProps = {
    userId?: number;
    activeStore: ActiveStore;
    isCompanyDashboardEnabled: boolean;
};

interface CompanyDashboardInterface {
    userId?: number;
    isCompanyDashboardEnabled: boolean;
    activeStore: ActiveStore;
    goToResumeRegistration: () => void;
    goToYourProfile: () => void;
    storeError: (error: string) => void;
    resetError: () => void;
    errorMessage?: string;
}

type CompanyDashboardQueryResult = {
    user: Pick<User, 'email' | 'company' | 'type'>;
};

/* eslint-disable no-underscore-dangle */
const CompanySizeMap = {
    [CompanySizeEnum._1]: '1 - 10',
    [CompanySizeEnum._11]: '11 - 50',
    [CompanySizeEnum._51]: '51 - 200',
    [CompanySizeEnum._201]: '201 - 500',
    [CompanySizeEnum._501]: '501 - 1000',
    [CompanySizeEnum._1001]: '1001+',
};

const CompanyDashboard = ({
    userId,
    isCompanyDashboardEnabled,
    activeStore,
    goToYourProfile,
    goToResumeRegistration,
    storeError,
    errorMessage,
    resetError,
}: CompanyDashboardInterface) => {
    const t = useTranslationCashHack(activeStore.country_code);

    const { loading: dashboardLoading, data: dashboardData, error: dashboardError } = useQuery<
        CompanyDashboardQueryResult
    >(CompanyDashboardQuery, { variables: { userId } });

    const isBusinessUser = dashboardData?.user.type === 'BUSINESS';
    const companyDataIsIncomplete =
        isBusinessUser &&
        (!dashboardData?.user.company || dashboardData?.user.company?.status === 'incomplete');

    const shouldShowSpinner =
        !isCompanyDashboardEnabled ||
        !dashboardData ||
        dashboardLoading ||
        companyDataIsIncomplete ||
        (!dashboardLoading && !isBusinessUser);

    const getTaxIDLabel = (activeStoreCode: string | undefined): string => {
        switch (activeStoreCode) {
            case 'business_us':
                return 'companyDashboard.taxid_us';
            case 'business_es':
                return 'companyDashboard.taxid_es';
            case 'business_at':
                return 'companyDashboard.taxid_at';
            case 'business_nl':
                return 'companyDashboard.taxid_nl';
            default:
                return 'companyDashboard.taxid_de';
        }
    };

    useEffect(() => {
        if (dashboardLoading) return;
        if (!isCompanyDashboardEnabled || !isBusinessUser) goToYourProfile();
        if (companyDataIsIncomplete) goToResumeRegistration();
    }, [
        isCompanyDashboardEnabled,
        isBusinessUser,
        dashboardLoading,
        companyDataIsIncomplete,
        goToYourProfile,
        goToResumeRegistration,
    ]);

    useEffect(() => {
        if (dashboardError) {
            storeError(t('company-dashboard.error'));
        } else if (errorMessage) {
            resetError();
        }
    }, [dashboardError, errorMessage, storeError, resetError, t]);

    if (shouldShowSpinner) {
        return <CircleLoader className={undefined} />;
    }

    return (
        <Container>
            <Content>
                <Link to={`${window.location.origin}/${activeStore.code}/your-profile`}>
                    <AccountButton type="button" iconPath={arrowBackIconPath}>
                        {t('companyDashboard.account_btn')}
                    </AccountButton>
                </Link>
                <CompanyInfo>
                    <Name>{t('yourProfile.companyDetailsTitle')}</Name>
                    <Text>{t('companyDashboard.description')}</Text>
                    <RequestChangeButton
                        type="button"
                        onClick={(event) => {
                            event.preventDefault();
                            sendAnalyticsEvent('Company Details Change Requested');
                            openHelpCenter();
                        }}
                    >
                        {t('companyDashboard.request_change_btn')}
                    </RequestChangeButton>
                </CompanyInfo>

                <CompanyInfoSection>
                    <CapitalLettersTitle>
                        {t('companyDashboard.company_info_title')}
                    </CapitalLettersTitle>
                    <CompanyInfoBox
                        icon={<CompanyIcon />}
                        title={t('companyDashboard.company_name')}
                        text={dashboardData?.user?.company?.name || ''}
                    />
                    <CompanyInfoBox
                        icon={<CompanyIcon />}
                        title={t('companyDashboard.company_type_title')}
                        text={dashboardData?.user?.company?.type.name || ''}
                    />
                    <CompanyInfoBox
                        icon={<OptionsIcon />}
                        title={t('companyDashboard.company_size_title')}
                        text={
                            dashboardData?.user?.company?.size
                                ? `${CompanySizeMap[dashboardData?.user?.company?.size]} ${t(
                                      'companyDashboard.employees',
                                  )}`
                                : ''
                        }
                    />
                    <CompanyInfoBox
                        icon={<Globe />}
                        title={t('companyDashboard.company_website_title')}
                        text={dashboardData?.user?.company?.website ?? '-'}
                    />
                </CompanyInfoSection>

                <Divider />

                <CompanyInfoSection>
                    <CapitalLettersTitle>
                        {t('companyDashboard.billing_information_title')}
                    </CapitalLettersTitle>
                    <CompanyInfoBox
                        icon={<CompanyBuildingIcon />}
                        title={t('companyDashboard.billing_address')}
                        address={
                            dashboardData?.user?.company?.address
                                ? dashboardData?.user?.company?.address
                                : undefined
                        }
                        text=""
                    />
                    <CompanyInfoBox
                        icon={<EnvelopeIcon />}
                        title={t('companyDashboard.billing_email')}
                        text={
                            dashboardData?.user?.company?.billingEmail ||
                            dashboardData?.user.email ||
                            ''
                        }
                    />
                    <CompanyInfoBox
                        icon={<TaxIDIcon />}
                        title={t(getTaxIDLabel(activeStore.code))}
                        text={dashboardData?.user?.company?.ust || '-'}
                    />
                </CompanyInfoSection>

                <Divider />

                <CompanyInfoSection>
                    <CapitalLettersTitle>
                        {t('companyDashboard.legal_information_title')}
                    </CapitalLettersTitle>
                    <CompanyInfoBox
                        icon={<UserIcon />}
                        title={t('company-dashboard.legal_rep_title')}
                        text={
                            dashboardData?.user?.company?.legalRepresentativeEmail ||
                            dashboardData?.user.email ||
                            ''
                        }
                        description={t('companyDashboard.legal_rep_description')}
                    />
                </CompanyInfoSection>
            </Content>
        </Container>
    );
};

const enhance = connect<
    {
        mainAppRoute: string;
        userId?: number;
        activeStore: ActiveStore;
        isCompanyDashboardEnabled: boolean;
        errorMessage?: string;
    },
    {
        goToYourProfile: (mainAppRoute: string) => void;
        goToResumeRegistration: (mainAppRoute: string, activeStore: ActiveStore) => void;
        storeError: (error: string) => void;
        resetError: () => void;
    },
    EnhancedProps & CompanyDashboardInterface,
    EnhancedProps & CompanyDashboardInterface
>(
    (state) => ({
        userId: userIdSelector(state),
        mainAppRoute: mainAppRouteSelector(state),
        activeStore: activeStoreSelector(state),
        isCompanyDashboardEnabled: companyDashboardEnabledSelector(state),
        errorMessage: errorMessageSelector(state),
    }),
    (dispatch) => ({
        goToYourProfile: (mainAppRoute: string): void => {
            dispatch(push({ pathname: `${mainAppRoute}/your-profile` }));
        },
        storeError: (error: string): void => {
            dispatch({ type: 'STORE_ERROR', data: error });
        },
        resetError: (): void => {
            dispatch({ type: 'RESET_ERROR', data: null });
        },
        goToResumeRegistration: (mainAppRoute: string, activeStore: ActiveStore): void =>
            window.location.assign(
                `${mainAppRoute}-${activeStore.activeLanguage}/auth?flow=MISSING_INFO_BUSINESS`,
            ),
    }),
    (
        { mainAppRoute, activeStore, ...restStateProps },
        { goToYourProfile, goToResumeRegistration, storeError, resetError },
        ownProps,
    ) => ({
        ...ownProps,
        ...restStateProps,
        mainAppRoute,
        activeStore,
        goToYourProfile: (): void => goToYourProfile(mainAppRoute),
        goToResumeRegistration: (): void => goToResumeRegistration(mainAppRoute, activeStore),
        storeError: (error: string): void => storeError(error),
        resetError: (): void => resetError(),
    }),
);

export default enhance(CompanyDashboard);
